import React from 'react';
import styles from './status-dropdown.styles.css';
import {
  addStatus,
  removeStatus,
  unremoveStatus,
} from 'src/resources/statuses.resource.js';
import { partial, sortBy } from 'lodash';
import Disposable from 'react-disposable-decorator';
import { catchAsyncStacktrace } from 'auto-trace';
import StatusCreation from './status-creation.component.js';
import { successToast } from 'toast-service!sofe';
import { CprTooltip } from 'canopy-styleguide!sofe';
import Color from 'color';

// This component, and what it uses, are being exported
// and are shared to other projects
@Disposable
export default class StatusDropdown extends React.Component {
  state = {
    create: false,
  };
  componentDidMount() {
    document.addEventListener('click', this.clickOff);
    this.dropdown.addEventListener('click', this.clickOn);
    this.setScroll();
  }
  componentWillUnmount() {
    document.removeEventListener('click', this.clickOff);
  }
  render() {
    // as of Dec 2019, client request statuses are limited to max 4 and task statuses are >= 8
    const taskIsClientRequest = this.props.statuses.length < 5
    const { statusNameMaxWidth, width, selectedStatus, alignDropdownRight } = this.props;
    const statuses = this.props.statuses.filter(
      status => ((status.archived && selectedStatus.id === status.id) || !status.archived)
    );
    const sortedStatuses = taskIsClientRequest
      ? statuses
      : sortBy(statuses, status => {
        if (!status.custom) return defaultStatusOrdering(status);
        else return `~${status.name.toLowerCase()}`;
        //The '~' character is alphabetically the last one
      });
    return (
      <div
        onContextMenu={e => e.stopPropagation()}
        onMouseDown={e => e.stopPropagation()}
        className={`cps-card__height-2 ${styles.statusDropdown}`}
        style={{right: alignDropdownRight ? 0 : "auto", width: width ? width : "248px"}}
        ref={ref => (this.dropdown = ref)}
      >
        <div
          ref={ref => (this.scrollBox = ref)}
          className={`${styles.scrollBox}`}
        >
          {sortedStatuses.map((status, index) => {
            const isLast = index === sortedStatuses.length - 1;
            const isSelected = status.id === selectedStatus.id;
            return (
              <div
                ref={ref => {
                  if (isSelected) {
                    this.selectedStatus = ref;
                  }
                }}
                key={status.id}
                onClick={partial(this.updateTaskStatus, status.id)}
                data-test-class={`status-${index}`}
                className={`${styles.status}`}
                style={isSelected ? lightBackground(status.color) : {}}
              >
                <div className={`${styles.statusInfo}`}>
                  <CprTooltip top={isLast ? -40 : null} html={status.name} delayTime="500">
                    <div
                      style={{ background: status.color }}
                      className={`${styles.statusDot} ${status.name == "No status" ? styles.noStatusBorder : ""}`}
                    />
                  </CprTooltip>
                  <div className="cps-ellipsis status-name" style={{maxWidth: statusNameMaxWidth ? statusNameMaxWidth : "calc(100% - 32px)"}}>
                    {status.name}
                  </div>
                </div>
                <span
                  style={{
                    opacity: status.custom ? 1 : 0,
                  }}
                  onClick={e => {
                    e.stopPropagation();
                    if (status.custom) {
                      this.removeStatus(status.id);
                    }
                  }}
                  className={`cps-icon cps-icon-sm-neg ${styles.deleteX}`}
                />
              </div>
            );
          })}
        </div>
        {!taskIsClientRequest &&
          <div
            onClick={() => {
              this.setState({
                create: true,
              });
            }}
            className={`${styles.status}`}
          >
            <div className={`${styles.statusInfo}`}>
              <div
                style={{ background: '#DFDFDF' }}
                className={`${styles.statusDot}`}
              />
              <div className={`cps-light-gray`} style={{ whiteSpace: "nowrap" }}>Add custom status</div>
            </div>
            <span
              style={{
                opacity: 0,
              }}
              className={`cps-icon cps-icon-close`}
            />
            {this.state.create &&
              <StatusCreation
                close={() => {
                  this.setState({
                    create: false,
                  });
                }}
                createStatus={this.createStatus}
              />}
          </div>
        }
      </div>
    );
  }
  clickOn = e => {
    this.dontClose = true;
  };
  clickOff = e => {
    if (!e || !this.dontClose) {
      this.props.close();
    }
    if (this.dontClose) {
      this.dontClose = false;
    }
  };
  updateTaskStatus = id => {
    this.props.updateTaskStatus(id);
    this.props.close();
  };
  removeStatus = id => {
    this.props.cancelWhenUnmounted(removeStatus(id).subscribe(statuses => {
      this.props.updateStatuses(statuses);
      successToast('Status removed', 'Undo', () => {
        this.clickOn();
        this.unremoveStatus(id);
      });
    }, catchAsyncStacktrace()));
  };
  unremoveStatus = id => {
    this.props.cancelWhenUnmounted(removeStatus(id).subscribe(statuses => {
      this.props.updateStatuses(statuses);
    }, catchAsyncStacktrace()));
  };
  createStatus = status => {
    this.props.cancelWhenUnmounted(
      addStatus(this.props.userId, status).subscribe(statuses => {
      this.props.updateStatuses(statuses);
    }, catchAsyncStacktrace()));
  };
  setScroll = () => {
    if (this.selectedStatus && this.scrollBox) {
      const selectedRect = this.selectedStatus.getBoundingClientRect();
      const scrollRect = this.scrollBox.getBoundingClientRect();
      if (scrollRect.bottom < selectedRect.bottom) {
        this.scrollBox.scrollTop = selectedRect.bottom - scrollRect.bottom;
      }
    }
  };
}

function defaultStatusOrdering(status) {
  if (status.name === 'No status') return '0';
  if (status.name === 'Not started') return '1';
  if (status.name === 'In progress') return '2';
  if (status.name === 'Review') return '3';
  if (status.name === 'On hold') return '4';
  if (status.name === 'Completed') return '5';

  return status.name;
}

function lightBackground(color) {
  const newColor = darkenLoop(Color(color).lighten(0.8));
  return {
    background: newColor.hex(),
  };
}

function darkenLoop(color) {
  if (color.lightness() > 95) return darkenLoop(color.darken(0.1));
  else return color;
}
